import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import CustomToggle from "../../../../components/CustomToggle/CustomToggle";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import useLessonPlanData from "./useLessonPlanData";
import { buildLessonPlanColumns } from "./lessonPlanColumns";
import LessonPlanHistoryPanel from "./LessonPlanHistoryPanel";

function ApprovedLessonPlan() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};
  const user = TokenService.getUser();
  const roleId = Number(user?.role?.id);
  const clientData = user?.client_data?.[0] || {};
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const staffId =
    clientData.master_id != null && clientData.master_id !== ""
      ? Number(clientData.master_id)
      : null;
  const hideActions = [4, 6].includes(roleId);

  // initial toggle = "all" (per requirement)
  const [weekFilter, setWeekFilter] = useState("all");

  // ─── PopupModal state ───
  const [modal, setModal] = useState({
    open: false,
    type: "",
    message: "",
  });

  const closeModal = () =>
    setModal({
      open: false,
      type: "",
      message: "",
    });

  const {
    data,
    loading,
    rowCount,
    pagination,
    setPagination,
    columnFilters,
    handleColumnFiltersChange,
    topicOptions,
    subTopicOptions,
    refetch,
  } = useLessonPlanData(2, weekFilter === "this_week" ? 1 : 0);

  const handleView = (row) =>{
    navigate("/user/lessonplanview", {
      state: { ID: row.def_id, data: row },
    })};

  // Select changed in the Approved/Completed status cell.
  // Only Approved → Completed is allowed. Triggers complete API immediately.
  const handleChangeStatus = (rowOriginal, nextLabel) => {
    if (String(nextLabel).toLowerCase() !== "completed") return;
    doStatusUpdate(rowOriginal);
  };

  const doStatusUpdate = async (rowOriginal) => {
    try {
      const payload = {
        hm_id: rowOriginal?._raw?.id || "",
        school_id: schoolId || "",
        staff_id: staffId || "",
        comments: "",
      };

      const response = await AxiosInstance.post(
        `/book/lessonPlanComplete`,
        payload,
      );

      if (response?.data?.status) {
        setModal({
          open: true,
          type: "success",
          message:
            response.data.message ||
            t.statusUpdateSuccess ||
            "Status updated successfully",
        });
        refetch();
      } else {
        setModal({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            t.statusUpdateFailed ||
            "Status update failed",
        });
      }
    } catch (e) {
      console.error("Lesson plan complete error:", e);
      setModal({
        open: true,
        type: "error",
        message: t.statusUpdateFailed || "Status update failed",
      });
    }
  };

  const handleDateRangePicked = () => {
    if (weekFilter !== "all") setWeekFilter("all");
  };

  const toggleOptions = [
    { label: t.toggleAll || "All", value: "all" },
    { label: t.toggleThisWeek || "This week", value: "this_week" },
  ];

  const columns = useMemo(() => {
    const cols = buildLessonPlanColumns({
      translations,
      topicOptions,
      subTopicOptions,
      includeTopic: false,        // 👈 hide Topic
      includeSubTopic: false,
      includePeriod: true,
      includeDay: true,
      includeSection: true,
      includeScheduledDate: true,
      includeStatus: true,
      approvedStatusCell: true, // renders Approved/Completed select dropdown
      onDateRangePicked: handleDateRangePicked,
      onChangeStatus: handleChangeStatus,
    });

    if (!hideActions) {
      cols.push({
        id: "actions",
        header: t.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={t.view || "View"}
          >
            <FaEye />
          </button>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations, topicOptions, subTopicOptions, hideActions, weekFilter]);

  return (
    <>
      <div className={styles.toggleRow}>
        <CustomToggle
          options={toggleOptions}
          value={weekFilter}
          onChange={setWeekFilter}
        />
      </div>

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
        enableExpanding={true}
        renderDetailPanel={({ row }) => <LessonPlanHistoryPanel row={row} />}
      />

      <PopupModal
        open={modal.open}
        title={
          modal.type === "success"
            ? t.modalSuccessTitle || "Success"
            : t.modalFailedTitle || "Failed"
        }
        message={modal.message}
        onOk={closeModal}
        onClose={closeModal}
      />
    </>
  );
}

export default ApprovedLessonPlan;
